import { forwardRef } from 'react';
import { Collapse, Paper, Stack } from '@rossum/ui/material';
import { borderRadius } from '@rossum/ui/theme';
import { TILE_NAVIGATE_CLASSNAME, TileActions } from './TileActions';
import { TileChip } from './TileChip';
import { TileContent } from './TileContent';
import { useTileDraggableContext } from './TileDraggableContext';
import { TileDragHandle } from './TileDragHandle';
import { TileIcon } from './TileIcon';
import { TILE_HEIGHT, TileProps } from './types';

export const Tile = forwardRef<HTMLDivElement, TileProps>(
  (
    {
      iconProps,
      title,
      description,
      inlineBadges,
      chips,
      endChips,
      quickActions,
      toggleAction,
      contextMenuItems,
      contextMenuNestedSection,
      dragHandle = <TileDragHandle />,
      to,
      expandable,
      disabled,
      'data-cy': dataCy,
      'data-id': dataId,
    },
    ref
  ) => {
    const dragContext = useTileDraggableContext();
    const isActive = dragContext?.isActive ?? false;

    const showDragHandle = dragHandle !== null && dragContext !== null;
    const isExpanded = expandable?.expanded ?? false;

    const tileHeader = (
      <Stack
        component={Paper}
        direction="row"
        alignItems="stretch"
        position="relative"
        gap={0.5}
        elevation={expandable ? 0 : 1}
        sx={{
          minHeight: TILE_HEIGHT,
          borderRadius: borderRadius('m'),
          ...(expandable && isExpanded
            ? {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              }
            : {}),
          opacity: disabled ? 0.5 : 1,
          backgroundColor: t =>
            isActive || isExpanded ? t.palette.action.hover : undefined,
          transition: t =>
            t.transitions.create('background-color', {
              duration: t.transitions.duration.short,
            }),
          cursor: expandable ? 'pointer' : undefined,
          '&:hover, &:focus-within': {
            backgroundColor: t => t.palette.action.hover,
            [`.${TILE_NAVIGATE_CLASSNAME}`]: {
              opacity: 1,
            },
          },
        }}
        onClick={expandable ? expandable.onToggleExpanded : undefined}
      >
        {/* Drag handle */}
        {showDragHandle && dragHandle}

        {/* Left icon */}
        {iconProps && (
          <Stack
            justifyContent="center"
            alignItems="center"
            sx={{ pl: showDragHandle ? 0 : 2, pr: 1 }}
          >
            <TileIcon {...iconProps} />
          </Stack>
        )}

        {/* Main content area */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          sx={{
            flex: 1,
            py: 1.5,
            pl: !iconProps && !showDragHandle ? 2 : 0,
            pr: 1,
            minWidth: 0,
          }}
        >
          {/* Content: title, description, chips */}
          <TileContent
            title={title}
            description={description}
            inlineBadges={inlineBadges}
            chips={chips}
            to={to}
            disabled={disabled}
          />

          {/* End chips (right side, before actions) */}
          {endChips && endChips.length > 0 && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              flexShrink={0}
            >
              {endChips.map(chip => (
                <TileChip key={chip.key} chip={chip} />
              ))}
            </Stack>
          )}

          {/* Actions area */}
          <TileActions
            quickActions={quickActions}
            toggleAction={toggleAction}
            contextMenuItems={contextMenuItems}
            contextMenuNestedSection={contextMenuNestedSection}
            to={to}
            expandable={expandable}
          />
        </Stack>
      </Stack>
    );

    if (expandable) {
      return (
        <Stack
          ref={ref}
          data-cy={dataCy}
          data-id={dataId}
          sx={{
            borderRadius: borderRadius('s'),
          }}
        >
          {tileHeader}
          <Collapse in={isExpanded} mountOnEnter>
            <Stack
              sx={{
                backgroundColor: t => t.palette.action.hover,
                border: t => `1px solid ${t.palette.divider}`,
                borderBottomLeftRadius: t =>
                  borderRadius.abs(t.shape.borderRadius, 's'),
                borderBottomRightRadius: t =>
                  borderRadius.abs(t.shape.borderRadius, 's'),
                borderTopLeftRadius: 0,
                borderTopRightRadius: 0,
                borderTop: 'none',
              }}
            >
              {expandable.content}
            </Stack>
          </Collapse>
        </Stack>
      );
    }

    return (
      <Stack ref={ref} data-cy={dataCy} data-id={dataId}>
        {tileHeader}
      </Stack>
    );
  }
);

Tile.displayName = 'Tile';
